import React, {useState, useEffect, useMemo} from "react";
import {useStore} from "../../../hooks/useStore";
import {compose, withProps} from "recompose";
import {Collapse, Row, Space} from "antd";
import apiKey from "../../../_core/apiKey";
import {
    InfoRow,
    InfoInput,
    InfoTypography,
    InfoText,
    InfoButton,
    RightBottomContainer,
    GroupColor,
    InfoWindowStyled
} from "./polyMapStyles";

import InfoWindowInput from "./InfoWindowInput";
import {colorPalette} from "../../../constants/constants";
import {
    GoogleMap,
    useJsApiLoader,
    InfoWindow,
    Polygon,
    Marker,
    LoadScript,
    HeatmapLayer
} from "@react-google-maps/api";
import {useTranslation} from "react-i18next";

const PolyMap = compose()(
    ({
        center,
        showInfo,
        showInfoWindow,
        zones,
        companyFilter,
        regionGroups,
        regionGroupsByCompany
    }) => {
        const stores = useStore();
        const {t} = useTranslation(["polyMap"]);
        const [inputValue, setInputValue] = useState("");
        const [groupValue, setGroupValue] = useState("");
        const [regionGroup, setRegionGroup] = useState("");
        const [currentZoom, setCurrentZoom] = useState(12);
        const [groupedZones, setGroupedZones] = useState({});
        const [coloredGroups, setColoredGroups] = useState({});
        const [coloredRegionGroups, setColoredRegionGroups] = useState({});
        const [regionGroupsWithColor, setRegionGroupsWithColor] = useState({});
        const [isCompanyFiltered, setIsCompanyFiltered] = useState(false);

        useEffect(() => {
            setGroupedZones({});
            zones?.map(zone => {
                if (zone.group) {
                    if (!groupedZones[zone.group]) {
                        groupedZones[zone.group] = [zone.name];
                    } else {
                        groupedZones[zone.group].push(zone.name);
                    }
                }
            });
            colorTheGroups();
        }, [zones]);

        const colorTheGroups = () => {
            const groupedZoneNames = Object.keys(groupedZones);
            groupedZoneNames.sort();
            let tempGroups = {};

            groupedZoneNames.map((zone, index) => {
                let newColor = colorPalette[index];
                tempGroups = {...tempGroups, [zone]: newColor};
            });
            setColoredGroups(tempGroups);
        };

        useEffect(() => {
            colorTheRegionGroups();
            makeRegionGroupsColorList();
        }, [regionGroupsByCompany]);

        const colorTheRegionGroups = () => {
            regionGroupsByCompany.sort((a, b) => a.name.localeCompare(b.name));
            let tempRegionGroups = [];
            regionGroupsByCompany.map((regionGroup, index) => {
                let newColor = colorPalette[index];
                tempRegionGroups.push({...regionGroup, color: newColor});
            });
            setRegionGroupsWithColor(tempRegionGroups);
        };

        const makeRegionGroupsColorList = () => {
            const regionGroupNames = regionGroupsByCompany.map(({name}) => name);
            regionGroupNames.sort();
            let tempRegionGroups = {};
            regionGroupNames.map((regionGroup, index) => {
                let newColor = colorPalette[index];
                tempRegionGroups = {...tempRegionGroups, [regionGroup]: newColor};
            });

            setColoredRegionGroups(tempRegionGroups);
        };

        const handleFillColorRegionGroups = (zone, rgByCompany) => {
            const foundRegionGroup = rgByCompany.find(regionGroup =>
                regionGroup.regions.includes(zone.id)
            );
            return foundRegionGroup ? foundRegionGroup.color : "#A1887F";
        };

        const handleUpdateName = async values => {
            if (inputValue.length > 0) {
                return stores.appStore.openConfirmModal({
                    title: t("modal.confirmTitle", {ns: "common"}),
                    content: t("polyMap.modal.handleUpdateName.content", {ns: "polyMap"}),
                    onOk: async () => {
                        await stores.regionStore.updateRegionName({values});
                        showInfoWindow(undefined);
                    }
                });
            }
            return stores.appStore.openErrorModal({
                content: t("polyMap.modal.handleUpdateName.errorContent", {ns: "polyMap"})
            });
        };

        const findGroupNameOfRegion = regionId => {
            const foundGroup = regionGroupsByCompany.find(regionGroup =>
                regionGroup.regions.includes(regionId)
            );
            return foundGroup ? foundGroup.name : "";
        };

        const handleUpdateRegionGroup = async values => {
            if (regionGroup.length > 0) {
                return stores.appStore.openConfirmModal({
                    title: t("modal.confirmTitle", {ns: "common"}),
                    content: t("polyMap.modal.handleUpdateRegionGroup.content", {ns: "polyMap"}),
                    onOk: async () => {
                        await stores.regionStore.addOrUpdateRegionGroups({
                            values
                        });
                        showInfoWindow(undefined);
                    }
                });
            }
            return stores.appStore.openErrorModal({
                content: t("polyMap.modal.handleUpdateRegionGroup.errorContent", {ns: "polyMap"})
            });
        };

        // Not used arrow func for reach "this"
        function handleZoomChanged() {
            setCurrentZoom(this.getZoom()); // current zoom
        }

        let responsive = {
            10: {font: 3, scale: 3},
            11: {font: 5, scale: 5},
            12: {font: 9, scale: 6},
            13: {font: 13, scale: 8},
            14: {font: 17, scale: 10},
            15: {font: 21, scale: 12}
        };
        const [libraries] = useState(["visualization"]);
        const {isLoaded} = useJsApiLoader({
            id: "google-map-script",
            googleMapsApiKey: apiKey,
            libraries
        });

        const groupNameByCompany = useMemo(() => {
            const result = {};
            for (const regionGroup of regionGroupsByCompany) {
                for (const region of regionGroup.regions) {
                    result[region._id] = regionGroup.name;
                }
            }

            return result;
        }, [regionGroupsByCompany]);

        return isLoaded ? (
            <GoogleMap
                // key={googleMapKey}
                mapContainerStyle={{
                    height: "100%",
                    width: "100%",
                    mapElement: <div style={{height: `100%`}} />,
                    containerElement: <div style={{height: `100%`}} />,
                    loadingElement: <div style={{height: `100%`}} />
                }}
                zoom={12}
                defaultCenter={center}
                // ?? default center
                center={center}
                onZoomChanged={handleZoomChanged}
                options={{
                    disableDefaultUI: true,
                    fullscreenControl: false,
                    gestureHandling: "greedy"
                }}
            >
                <HeatmapLayer data={[]} />
                {zones?.map((zone, index) => {
                    return (
                        <>
                            {currentZoom > 8 && (
                                <>
                                    <Marker
                                        key={index}
                                        data-testid="marker"
                                        options={{
                                            fullscreenControl: false,
                                            gestureHandling: "greedy"
                                        }}
                                        position={{
                                            lat: zone.center.coordinates[1],
                                            lng: zone.center.coordinates[0]
                                        }}
                                        label={{
                                            text: groupNameByCompany[zone.id] || zone.name,
                                            color: "white",
                                            fontSize: `${
                                                responsive[currentZoom] && zones.length > 1
                                                    ? responsive[currentZoom]["font"]
                                                    : responsive[15]["font"]
                                            }px`,
                                            fontWeight: "bold"
                                        }}
                                        icon={{
                                            path: `M ${`-${zone.name.length / 2}`}, -1.5 ${zone.name
                                                .length / 2}, -1.5 ${zone.name.length /
                                                2}, 1.5 ${`-${zone.name.length / 2}`}, 1.5 z`,
                                            strokeColor: "transparent",
                                            fillColor: "#000",
                                            fillOpacity: 0.6,
                                            scale:
                                                responsive[currentZoom] && zones.length > 1
                                                    ? responsive[currentZoom]["scale"]
                                                    : responsive[15]["scale"]
                                        }}
                                        onClick={() => {
                                            showInfoWindow(index);
                                        }}
                                    >
                                        {showInfo === index &&
                                            (stores.meStore.role == "superadmin" ||
                                                stores.meStore.role == "admin") &&
                                            !companyFilter && (
                                                <InfoWindowStyled
                                                    key={index}
                                                    onCloseClick={() => showInfoWindow(undefined)}
                                                >
                                                    <InfoRow>
                                                        <InfoTypography>
                                                            {t("fields.region", {ns: "common"})}:
                                                        </InfoTypography>

                                                        <InfoWindowInput
                                                            dataTestid="region"
                                                            placeholder={zone.name}
                                                            inputValue={inputValue}
                                                            setInputValue={setInputValue}
                                                        />

                                                        <InfoTypography>
                                                            {t("fields.upperGroup", {ns: "common"})}
                                                            :
                                                        </InfoTypography>

                                                        <InfoWindowInput
                                                            dataTestid="upperGroup"
                                                            placeholder={zone?.group}
                                                            inputValue={groupValue}
                                                            setInputValue={setGroupValue}
                                                        />

                                                        <InfoButton
                                                            type={"primary"}
                                                            data-testid="submitButton"
                                                            onClick={() =>
                                                                handleUpdateName({
                                                                    name: inputValue,
                                                                    group: groupValue || null,
                                                                    regionsId: zone.id
                                                                })
                                                            }
                                                        >
                                                            {t("actions.save", {ns: "common"})}
                                                        </InfoButton>
                                                    </InfoRow>
                                                </InfoWindowStyled>
                                            )}

                                        {showInfo === index &&
                                            (stores.meStore.role == "superadmin" ||
                                                stores.meStore.role == "admin") &&
                                            !!companyFilter && (
                                                <InfoWindowStyled
                                                    key={index}
                                                    onCloseClick={() => showInfoWindow(undefined)}
                                                >
                                                    <InfoRow>
                                                        <Space direction={"vertical"}>
                                                            <Space direction={"horizontal"}>
                                                                <InfoTypography>
                                                                    {t("fields.region", {
                                                                        ns: "common"
                                                                    })}
                                                                    :
                                                                </InfoTypography>
                                                                <InfoTypography>
                                                                    {zone.name}
                                                                </InfoTypography>
                                                            </Space>
                                                            <Space direction={"horizontal"}>
                                                                <InfoTypography>
                                                                    {t("fields.upperGroup", {
                                                                        ns: "common"
                                                                    })}
                                                                    :
                                                                </InfoTypography>
                                                                <InfoTypography>
                                                                    {zone?.group}
                                                                </InfoTypography>
                                                            </Space>
                                                            <InfoTypography>
                                                                {t("fields.regionGroup", {
                                                                    ns: "common"
                                                                })}
                                                                :
                                                            </InfoTypography>
                                                            <InfoWindowInput
                                                                placeholder={findGroupNameOfRegion(
                                                                    zone.id
                                                                )}
                                                                inputValue={regionGroup}
                                                                setInputValue={setRegionGroup}
                                                            />
                                                        </Space>
                                                        <InfoButton
                                                            type={"primary"}
                                                            data-testid="submitButton"
                                                            onClick={() =>
                                                                handleUpdateRegionGroup({
                                                                    name: regionGroup,
                                                                    companyId: companyFilter,
                                                                    regionId: zone.id
                                                                })
                                                            }
                                                        >
                                                            {t("actions.save", {ns: "common"})}
                                                        </InfoButton>
                                                    </InfoRow>
                                                </InfoWindowStyled>
                                            )}
                                    </Marker>
                                    {!companyFilter ? (
                                        <Polygon
                                            data-testid="polygon"
                                            key={zone.center[0]}
                                            path={zone.zoneData}
                                            options={{
                                                strokeColor: zone.group
                                                    ? coloredGroups[zone.group]
                                                    : "#A1887F",
                                                strokeOpacity: 1,
                                                strokeWeight: 2,
                                                fillColor: zone.group
                                                    ? coloredGroups[zone.group]
                                                    : "#A1887F",
                                                fillOpacity: 0.5,
                                                name: zone.name,
                                                clicked: false
                                            }}
                                        />
                                    ) : (
                                        <Polygon
                                            key={zone.center[0]}
                                            path={zone.zoneData}
                                            options={{
                                                strokeColor: zone.group
                                                    ? coloredGroups[zone.group]
                                                    : "#A1887F",
                                                strokeOpacity: 1,
                                                strokeWeight: 2,
                                                fillColor: handleFillColorRegionGroups(
                                                    zone,
                                                    regionGroupsWithColor
                                                ),
                                                fillOpacity: 0.5,
                                                name: zone.name,
                                                clicked: false
                                            }}
                                        />
                                    )}
                                </>
                            )}
                        </>
                    );
                })}

                <RightBottomContainer data-testid="regionColorsCard">
                    <Collapse>
                        <Collapse.Panel
                            header={
                                <span data-testid="regionColorsButton">
                                    {!!companyFilter
                                        ? t("polyMap.fields.regionGroupColors", {ns: "polyMap"})
                                        : t("polyMap.fields.regionColors", {ns: "polyMap"})}
                                </span>
                            }
                            style={{maxHeight: "60vh", overflow: "auto"}}
                        >
                            {Object.values(
                                !!companyFilter ? coloredRegionGroups : coloredGroups
                            )?.map((color, i) => {
                                return (
                                    <Row key={color + i} data-testid="color" data-testvalue={color}>
                                        <GroupColor color={color} data-testid="groupColor" />
                                        {!!companyFilter
                                            ? Object.keys(coloredRegionGroups)[i]
                                            : Object.keys(coloredGroups)[i]}
                                    </Row>
                                );
                            })}
                        </Collapse.Panel>
                    </Collapse>
                </RightBottomContainer>
            </GoogleMap>
        ) : (
            <></>
        );
    }
);

export default PolyMap;
